<template src='./project-appropriations.html'></template>

<script>
import Vue from 'vue'
import ContextPage from '@/components/misc/context-page/ContextPage.vue'
import tabulatorMixin from '@/mixins/tabulator'
import Tabulator from '@/components/tabulator/Tabulator'
import { EventBus } from '@/router/bus'
import { authorizedOrganizationId, hasProjectManageClaim } from '@/router/auth'

export default {
  components: {
    ContextPage,
    Tabulator
  },
  mixins: [tabulatorMixin],
  data () {
    return {
      projectCode: this.$route.params.projectCode,
      data: [],
      project: {},
      ProjectStatus: {
        PENDING: 0,
        FINISHED: 1
      },
      projectAppropriations: [],
      selectedAppropriations: false,
      tabulatorOptions: {
        ajaxURL: Vue.$config.api_root + '/' + Vue.$apiConfig.pagedProjectAppropriationsGet.replace('{projectCode}', this.$route.params.projectCode),
        ajaxResponse: (url, params, response) => {
          this.project = response.additionalInformation.project
          this.showLoader(false)
          this.pageLoader = false
          this.rowCount = response.additionalInformation.totalRows
          this.projectAppropriations = response.additionalInformation.projectAppropriationIds
          const retObj = {
            contentType: 'application/json; charset=utf-8',
            data: response.payload,
            last_page: this.getPageCount(this.rowCount)
          }
          if (this.switchTabulatorPage !== false && this.$refs.tabulator.getInstance === 'function') {
            this.$refs.tabulator.getInstance().modules.page.page = this.switchTabulatorPage
            this.switchTabulatorPage = false
          }
          const totalElement = document.getElementsByClassName('total-rows-number')
          if (totalElement.length && this.rowCount) {
            totalElement[0].innerHTML = this.rowCount
          } else {
            totalElement[0].innerHTML = '-'
          }
          return retObj
        },
        dataLoaded: (data) => {
          var rowsToSelect = data.filter(row => this.projectAppropriations.map(x => x).includes(row.appropriationId))
          rowsToSelect.forEach(row => {
            this.tabulatorInstance.selectRow(row.id)
          })
        },
        columns: [
          {
            formatter: 'rowSelection',
            titleFormatter: 'rowSelection',
            field: 'selected',
            visible: !this.canManageAppropriation,
            hozAlign: 'center',
            headerSort: false,
            width: 50
          },
          { title: '', field: 'id', visible: false, headerSort: false },
          {
            title: this.$t('appropriations.budgetYearShort'),
            field: 'budgetYear',
            width: 100,
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vYearSelect,
            headerFilterLiveFilter: false,
            hozAlign: 'center'
          },
          {
            title: this.$t('appropriations.organizationCode'),
            field: 'organizationCode',
            width: 100,
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vTextFieldFilter,
            headerFilterLiveFilter: false,
            hozAlign: 'center',
            tooltip: function (e, cell, onRendered) {
              return e._cell.row.getData().organization
            }
          },
          {
            title: this.$t('appropriations.program'),
            field: 'programCode',
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vTextFieldFilter,
            headerFilterLiveFilter: false,
            hozAlign: 'center',
            tooltip: function (e, cell, onRendered) {
              return e._cell.row.getData().program
            }
          },
          {
            title: this.$t('appropriations.project'),
            field: 'projectCode',
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vTextFieldFilter,
            headerFilterLiveFilter: false,
            hozAlign: 'center',
            tooltip: function (e, cell, onRendered) {
              return e._cell.row.getData().project
            }
          },
          {
            title: this.$t('appropriations.function'),
            field: 'functionCode',
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vTextFieldFilter,
            headerFilterLiveFilter: false,
            hozAlign: 'center',
            tooltip: function (e, cell, onRendered) {
              return e._cell.row.getData().function
            }
          },
          {
            title: this.$t('appropriations.sourceOfFunding'),
            field: 'sourceOfFundingCode',
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vTextFieldFilter,
            headerFilterLiveFilter: false,
            hozAlign: 'center',
            tooltip: function (e, cell, onRendered) {
              return e._cell.row.getData().sourceOfFunding
            }
          },
          {
            title: this.$t('appropriations.economicClassification'),
            field: 'economicClassificationCode',
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vTextFieldFilter,
            headerFilterLiveFilter: false,
            hozAlign: 'center',
            tooltip: function (e, cell, onRendered) {
              return e._cell.row.getData().economicClassification
            }
          },
          {
            title: this.$t('appropriations.currentAmount'),
            field: 'currentAmount',
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vMoneyRangeFilter,
            headerFilterLiveFilter: false,
            headerFilterParams: { isRange: true },
            hozAlign: 'right',
            formatter: (cell, formatterParams, onRendered) => {
              return cell.getValue() ? this.formatAmount(cell.getValue()) : 0
            }
          }
        ],
        rowClick: function (e, row) {
          row.toggleSelect()
        }
      }
    }
  },
  computed: {
    canManageAppropriation () {
      return hasProjectManageClaim() && authorizedOrganizationId() === this.project.organizationId && this.project.status === this.ProjectStatus.PENDING
    }
  },
  mounted () {
    EventBus.$emit('reload-crumbs')
  },
  methods: {
    filterAppropriations (selected) {
      if (selected) {
        this.tabulatorInstance.setFilter('selected', '=', true)
      } else {
        this.tabulatorInstance.removeFilter('selected', '=', true)
      }
    },
    setAppropriations () {
      const selectedRows = this.tabulatorInstance.getSelectedData()
      const pageRows = this.tabulatorInstance.getData()
      const added = selectedRows.filter(x => !this.projectAppropriations.includes(x.appropriationId)).map(x => x.appropriationId)
      const removed = this.projectAppropriations.filter(x => pageRows.map(x => x.appropriationId).includes(x) && !selectedRows.map(x => x.appropriationId).includes(x))
      if (added.length === 0 && removed.length === 0) {
        EventBus.$emit('alert', {
          variant: 'warning',
          message: this.$t('appropriations.setAppropriationsWarning'),
          errors: []
        })
        return
      }
      this.showLoader(true)
      this.$http.put(Vue.$apiConfig.setProjectAppropriationsPut.replace('{projectCode}', this.projectCode), { Add: added, Remove: removed }).then(
        response => {
          EventBus.$emit('alert', {
            variant: 'success',
            message: this.$t('appropriations.setAppropriationsSuccessful'),
            errors: []
          })
          this.showLoader(false)
        },
        error => {
          this.formatErrorResponse(error)
          this.showLoader(false)
        })
    },
    showLoader  (loader) {
      EventBus.$emit('projectLoader', loader)
    }
  }
}
</script>
